import React, { useState } from 'react';
import { Button } from './ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  const { login, loading } = useAuth();
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
        {/* Logo and Title - Centered */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Bible Thumper Logo" 
              className="h-24 w-24 object-contain"
            />
          </div>

          <h1 className="text-3xl font-bold text-purple-900 mb-2">
            Bible Thumper
          </h1>
          <p className="text-lg text-purple-700 mb-8">
            Teen Edition
          </p>

          {/* Description */}
          <p className="text-gray-600 mb-4">
            Welcome to your scripture memory journey! Sign in with Google to save your progress and continue your study.
          </p>
        </div>

        {/* Login Button */}
        <Button
          onClick={login}
          disabled={loading}
          size="lg"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mb-6"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Redirecting to Google...
            </>
          ) : (
            <>
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </>
          )}
        </Button>

        {/* Footer */}
        <p className="text-sm text-gray-500 text-center mb-6">
          Your progress will be securely saved and accessible from any device.
        </p>

        {/* Need Help? Link */}
        <div className="text-center">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium text-sm transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            Need help signing in?
            {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Help/FAQ Accordion Section */}
        {showHelp && (
          <div className="mt-6 border-t pt-6">
            <h2 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Login Help & Troubleshooting
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              {/* Safari Cookie Settings */}
              <AccordionItem value="safari">
                <AccordionTrigger className="text-left font-semibold text-purple-800">
                  Safari (iPhone/iPad) Cookie Settings
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-gray-700">
                    <p className="font-medium">If you're having trouble logging in on Safari, check these settings:</p>
                    
                    <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Step 1: Basic Cookie Settings</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Open <strong>Settings</strong> app</li>
                        <li>Scroll down and tap <strong>Safari</strong></li>
                        <li>Under "Privacy & Security" section</li>
                        <li>Make sure <strong>"Block All Cookies"</strong> is turned <strong>OFF</strong></li>
                      </ol>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Step 2: Advanced Cookie Settings</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>In Safari settings, tap <strong>Advanced</strong></li>
                        <li>Tap <strong>Website Data</strong></li>
                        <li>If issues persist, tap <strong>Remove All Website Data</strong></li>
                        <li>Confirm and try logging in again</li>
                      </ol>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Step 3: Cross-Site Tracking</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>In Safari settings</li>
                        <li>Find <strong>"Prevent Cross-Site Tracking"</strong></li>
                        <li>Try turning this <strong>OFF</strong> temporarily</li>
                        <li>Attempt to log in again</li>
                      </ol>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Chrome Mobile Cookie Settings */}
              <AccordionItem value="chrome">
                <AccordionTrigger className="text-left font-semibold text-purple-800">
                  Chrome (Mobile) Cookie Settings
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-gray-700">
                    <div className="bg-green-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Enable Cookies in Chrome Mobile:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Open Chrome app</li>
                        <li>Tap the three dots menu (⋮) in the top right</li>
                        <li>Tap <strong>Settings</strong></li>
                        <li>Tap <strong>Site settings</strong></li>
                        <li>Tap <strong>Cookies</strong></li>
                        <li>Ensure "Cookies" are <strong>enabled</strong></li>
                        <li>Make sure "Block third-party cookies" is <strong>OFF</strong></li>
                      </ol>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Clear Chrome Data:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>Go to Settings → Privacy</li>
                        <li>Tap <strong>Clear browsing data</strong></li>
                        <li>Select "Cookies and site data"</li>
                        <li>Tap <strong>Clear data</strong></li>
                      </ol>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* General Login Issues */}
              <AccordionItem value="general">
                <AccordionTrigger className="text-left font-semibold text-purple-800">
                  General Login Troubleshooting
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-gray-700">
                    <div className="bg-purple-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Common Issues & Solutions:</p>
                      
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium text-purple-900">🔄 Blank Page After Clicking Login</p>
                          <ul className="list-disc list-inside ml-2 text-sm">
                            <li>Clear your browser cache and cookies</li>
                            <li>Try opening in a private/incognito window</li>
                            <li>Make sure JavaScript is enabled</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-medium text-purple-900">🔁 Redirect Loop (Login → Login)</p>
                          <ul className="list-disc list-inside ml-2 text-sm">
                            <li>Clear all website data as described above</li>
                            <li>Close browser completely and reopen</li>
                            <li>Try a different browser</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-medium text-purple-900">⚠️ "Authentication Failed" Error</p>
                          <ul className="list-disc list-inside ml-2 text-sm">
                            <li>Make sure you're using the correct Google account</li>
                            <li>Check your internet connection</li>
                            <li>Try again in a few minutes</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-medium text-purple-900">📱 Mobile-Specific Issues</p>
                          <ul className="list-disc list-inside ml-2 text-sm">
                            <li>Update your browser to the latest version</li>
                            <li>Restart your device</li>
                            <li>Try using your device's default browser</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <p className="font-semibold text-yellow-900">💡 Pro Tip:</p>
                      <p className="text-sm mt-1">After changing cookie settings, completely close your browser and reopen it before trying to log in again.</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Still Having Issues */}
              <AccordionItem value="contact">
                <AccordionTrigger className="text-left font-semibold text-purple-800">
                  Still Having Issues?
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-gray-700">
                    <p>If you've tried all the steps above and still can't log in:</p>
                    
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <p className="font-semibold">Try These Alternative Solutions:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Use a different device (tablet, computer, different phone)</li>
                        <li>Try a different web browser</li>
                        <li>Connect to a different Wi-Fi network or use mobile data</li>
                        <li>Check if your browser has any ad-blockers or privacy extensions that might interfere</li>
                      </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                      <p className="font-semibold text-purple-900">📧 Need More Help?</p>
                      <p className="text-sm mt-2">Please note the following when seeking support:</p>
                      <ul className="list-disc list-inside text-sm ml-2 mt-1">
                        <li>Which device and browser you're using</li>
                        <li>Any error messages you see</li>
                        <li>What steps you've already tried</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
